import { FireIcon } from '@heroicons/react/24/outline'
import { useEffect, useMemo, useRef, useState } from 'react'
import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet'
import HeatmapLayer from 'Util/HeatmapLayer.tsx'
import { getBoundsFromCoordinates, getMapCenterFromCoordinates } from 'Util/MapCentroid.ts'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

import icon from 'leaflet/dist/images/marker-icon.png'

import iconShadow from 'leaflet/dist/images/marker-shadow.png'
import { geoJson2Normal } from 'Model/index.ts'
import React from 'react'
import { MapPinIcon } from '@heroicons/react/24/outline'

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41], // size of the icon
  iconAnchor: [12.5, 41]
})

L.Marker.prototype.options.icon = DefaultIcon

interface MapProps<T> {
  objects: T[]
  getCoordinates: (obj: T) => [number, number] | null
  getIntensity: (obj: T) => number
  getMarkerLabel: (obj: T) => string
  renderControls?: (heatmapVisible: boolean) => React.ReactNode
}

function Map<T>(props: MapProps<T> & React.HTMLAttributes<HTMLDivElement>) {
  const [showHeatmap, setShowHeatmap] = useState(false)
  const mapRef = useRef<L.Map | null>(null)

  const coordinates = useMemo(() => {
    return props.objects
      .map((o) => props.getCoordinates(o))
      .filter((coords): coords is [number, number] => Array.isArray(coords) && coords.length >= 2)
  }, [props.objects, props.getCoordinates])

  useEffect(() => {
    if (mapRef.current) {
      const zoom = mapRef.current.getZoom()
      mapRef.current.setView(getMapCenterFromCoordinates(coordinates, [52.736497054, 9.751163662]), zoom)
      mapRef.current.setZoomAround(getMapCenterFromCoordinates(coordinates, [52.736497054, 9.751163662]), zoom)
      mapRef.current.invalidateSize()
      // const bounds = getBoundsFromCoordinates(coordinates)
      // if (bounds) mapRef.current.fitBounds(bounds, { padding: [20, 20] })
    }
  }, [coordinates, mapRef])

  const handleFitAllSamples = () => {
    if (mapRef.current && coordinates.length > 0) {
      const bounds = getBoundsFromCoordinates(coordinates)
      if (bounds) mapRef.current.fitBounds(bounds, { padding: [20, 20] })
    }
  }

  return (
    <div className={(props.className || 'h-full') + ' relative'} style={props.style}>
      <button
        type="button"
        aria-label="Toggle Heatmap"
        className="btn btn-xs btn-circle absolute top-2 right-2 z-1000 bg-white shadow-sm"
        style={{ lineHeight: 0 }}
        onClick={() => setShowHeatmap((prev) => !prev)}
      >
        <FireIcon className={`w-5 h-5 ${showHeatmap ? 'text-red-500' : 'text-gray-500'}`} />
      </button>

      <button
        type="button"
        role="button"
        className="btn btn-xs btn-circle absolute top-10 right-2 z-100 bg-white shadow-sm"
        onClick={handleFitAllSamples}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 9V5a1 1 0 011-1h4M20 9V5a1 1 0 00-1-1h-4M4 15v4a1 1 0 001 1h4M20 15v4a1 1 0 01-1 1h-4"
          />
        </svg>
      </button>

      <MapContainer
        style={{ height: '100%', width: '100%', zIndex: 10 }}
        ref={mapRef}
        center={[52.736497054, 9.751163662]}
        zoom={13}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {props.renderControls && props.renderControls(showHeatmap)}
        {!showHeatmap &&
          props.objects.map((obj, index) => {
            const coords = props.getCoordinates(obj) || null
            if (!coords) return null
            return (
              <Marker key={index} position={geoJson2Normal(coords)}>
                <Popup maxWidth={600}>{props.getMarkerLabel(obj)}</Popup>
              </Marker>
            )
          })}
        {showHeatmap && (
          <HeatmapLayer
            points={props.objects
              .map((obj) => {
                const coords = props.getCoordinates(obj)?.flatMap((c) => c)
                if (!coords) return null
                return [coords[1], coords[0], props.getIntensity(obj)] as [number, number, number] // openstreetmap uses [lat, lng] order, we use [lng, lat] in our model
              })
              .filter((point) => !!point)}
          />
        )}
      </MapContainer>

      {/* Overlay for no coordinates message */}
      {coordinates.length === 0 && (
        <div className="absolute inset-0 flex flex-col items-center justify-center z-1000 pointer-events-none">
          <MapPinIcon className="h-16 w-16 mb-4 text-gray-500" />
          <p className="text-gray-500 text-center px-4">No coordinates available to display on the map.</p>
        </div>
      )}
    </div>
  )
}
export default Map
